import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Eye, Calendar, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { resolveAssetUrl } from '../lib/assets';

const REGION_NAMES = {
  riga: 'Rīga', jurmala: 'Jūrmala', liepaja: 'Liepāja', ventspils: 'Ventspils',
  daugavpils: 'Daugavpils', rezekne: 'Rēzekne', jelgava: 'Jelgava', jekabpils: 'Jēkabpils',
  valmiera: 'Valmiera', ogre: 'Ogre', tukums: 'Tukums', talsi: 'Talsi', saldus: 'Saldus',
  kuldiga: 'Kuldīga', dobele: 'Dobele', bauska: 'Bauska', aizkraukle: 'Aizkraukle',
  aluksne: 'Alūksne', balvi: 'Balvi', cesis: 'Cēsis', gulbene: 'Gulbene', kraslava: 'Krāslava',
  limbazi: 'Limbaži', ludza: 'Ludza', madona: 'Madona', preili: 'Preiļi', sigulda: 'Sigulda',
  smiltene: 'Smiltene', valka: 'Valka', marupe: 'Mārupe', olaine: 'Olaine', salaspils: 'Salaspils', adazi: 'Ādaži'
};

const CATEGORY_COLORS = {
  shop: 'bg-[#D97736]',
  service: 'bg-[#4A6B53]',
  event: 'bg-[#6366f1]',
  project: 'bg-[#8b5cf6]'
};

const SUBCATEGORY_LABELS = {
  lv: {
    food: 'Pārtika', materials: 'Materiāli', handcraft: 'Rokdarbi', rental: 'Noma',
    beauty: 'Skaistumkopšana', business_networking: 'Bizness un networking', charity: 'Labdarība un kopiena',
    community: 'Kopienas projekti', company_types: 'Uzņēmuma veids', construction: 'Būvniecība un remonts',
    creative: 'Radošie projekti', culture_art: 'Kultūra un māksla', education: 'Izglītība',
    events_catering: 'Pasākumu organizēšana', family: 'Ģimenes pasākumi', food_drink: 'Ēdiens un dzērieni',
    health: 'Veselība', home_garden: 'Māja un dārzs', industry: 'Nozare', it_digital: 'IT un digitālie',
    legal_finance: 'Juridiskie un finanšu', music: 'Mūzika', nightlife: 'Naktsdzīve',
    photo_video: 'Foto un video', sports_active: 'Sports un aktīvā atpūta', startup: 'Startup / biznesa projekti',
    transport: 'Transports un pārvadājumi',
    // legacy aliases + generic
    construction_repair: 'Būvniecība un remonts', food_drinks: 'Ēdiens un dzērieni', other: 'Cits',
  },
  en: {
    food: 'Food', materials: 'Materials', handcraft: 'Handcraft', rental: 'Rental',
    beauty: 'Beauty', business_networking: 'Business & Networking', charity: 'Charity & Community',
    community: 'Community Projects', company_types: 'Company Type', construction: 'Construction & Repair',
    creative: 'Creative Projects', culture_art: 'Culture & Art', education: 'Education',
    events_catering: 'Event Planning', family: 'Family Events', food_drink: 'Food & Drink',
    health: 'Health', home_garden: 'Home & Garden', industry: 'Industry', it_digital: 'IT & Digital',
    legal_finance: 'Legal & Finance', music: 'Music', nightlife: 'Nightlife',
    photo_video: 'Photo & Video', sports_active: 'Sports & Active Leisure', startup: 'Startup / Business Projects',
    transport: 'Transport & Moving',
    construction_repair: 'Construction & Repair', food_drinks: 'Food & Drink', other: 'Other',
  }
};

export default function ListingCard({ listing }) {
  const { t, language } = useLanguage();

  const formatPrice = () => {
    if (!listing.price && listing.price !== 0) return null;
    if (listing.price_type === 'free') return t('free');
    if (listing.price_type === 'negotiable') return t('negotiable');
    if (listing.price_type === 'hourly') return `€${listing.price}/h`;
    return `€${listing.price}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const defaultImage = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop';
  const imgUrl = resolveAssetUrl(listing.images?.[0] || defaultImage);

  return (
    <Link
      to={`/listing/${listing.slug || listing.id}`}
      className="group block bg-white border border-[#E5E4E0] rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Image — uniform 4:3 box for every card. A blurred copy FILLS the box so
          there are no empty bars, while the full image sits on top fully visible
          (no crop). Tidy grid + whole image, both at once. */}
      <div className="relative aspect-[4/3] overflow-hidden bg-[#F3F2EE]">
        <img
          src={imgUrl}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-60"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <img
          src={imgUrl}
          alt={listing.title}
          className="relative w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <Badge
          className={`absolute top-3 left-3 ${CATEGORY_COLORS[listing.category]} text-white border-0`}
        >
          {t(listing.category)}
        </Badge>
        {listing.subcategory && SUBCATEGORY_LABELS[language]?.[listing.subcategory] && (
          <Badge className="absolute top-3 left-24 max-w-[55%] truncate bg-white/80 backdrop-blur-sm text-[#121212] border-0 text-xs">
            {SUBCATEGORY_LABELS[language][listing.subcategory]}
          </Badge>
        )}
        {formatPrice() && (
          <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-[#121212]">
            {formatPrice()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-outfit font-semibold text-[#121212] text-lg mb-2 line-clamp-1 group-hover:text-[#D97736] transition-colors">
          {listing.title}
        </h3>
        <p className="text-[#5C5C5C] text-sm line-clamp-2 mb-3 font-figtree">
          {listing.description}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between text-xs text-[#5C5C5C]">
          <div className="flex items-center gap-1 min-w-0">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              {REGION_NAMES[listing.region] || listing.region}
              {listing.location_district ? ` · ${listing.location_district}` : ''}
            </span>
            {listing.distance_m != null && (
              <span className="ml-1 shrink-0 font-semibold text-[#D97736]" data-testid="listing-distance">
                · {listing.distance_m < 1000 ? `${listing.distance_m} m` : `${(listing.distance_m / 1000).toFixed(1)} km`}
              </span>
            )}
          </div>
          
          {listing.category === 'event' && listing.event_date ? (
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              <span>{formatDate(listing.event_date)}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              <span>{listing.views}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
